const TOKEN_KEY = 'anearAuthMeta'
const GUEST_IDENTITY_SNAPSHOT_KEY = 'anearGuestIdentitySnapshot'
const VENUE_HOME_CONTEXT_KEY = 'anearVenueHomeContext'
const OAUTH_RETURN_TARGET_KEY = 'anearOauthReturnTarget'
const LEGACY_GUEST_SELFIE_PREFIX = 'anear.guestHasSelfie:'

export default class TokenStorage {

  setAuthMeta = (authMeta) => {
    // Saves user token to localStorage
    localStorage.setItem(TOKEN_KEY, JSON.stringify(authMeta))
  }

  getAuthMeta = () => {
    // Retrieves auth metadata from localStorage.
    const raw = this.getTokenKey()
    if (!raw) return null

    try {
      return JSON.parse(raw)
    } catch (error) {
      return null
    }
  }

  getTokenKey= () => {
    return localStorage.getItem(TOKEN_KEY)
  }

  hasGuestToken = () => {
    return this.hasTokenOfType("guest")
  }

  hasRegisteredToken = () => {
    return this.hasTokenOfType("registered")
  }

  hasTokenOfType = acctType => {
    const meta = this.getAuthMeta()
    return meta && meta['auth-token'] && meta['acct-type'] === acctType
  }

  authToken = () => {
    const meta = this.getAuthMeta()
    return meta && meta['auth-token']
  }

  acctType = () => {
    const meta = this.getAuthMeta()
    return meta && meta['acct-type']
  }

  decodeAuthTokenPayload = () => {
    const token = this.authToken()
    if (!token) return null

    const parts = token.split('.')
    if (parts.length !== 3) return null

    try {
      const base64Payload = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/')
      return JSON.parse(atob(base64Payload))
    } catch (error) {
      return null
    }
  }

  isStoredAuthTokenExpired = (nowMs = Date.now()) => {
    const token = this.authToken()
    if (!token) return true

    const payload = this.decodeAuthTokenPayload()
    if (!payload) return true

    const { exp } = payload
    if (typeof exp !== 'number' || !Number.isFinite(exp)) {
      return false
    }

    // A guest JWT stays sendable after exp so ANAPI can ask for re-establish.
    if (this.hasGuestToken()) return false

    return exp * 1000 <= nowMs
  }

  clearAuthMeta = () => {
    // Clear user token and profile data from localStorage
    localStorage.removeItem(TOKEN_KEY)
    this.clearVenueHomeContext()
  }

  setVenueHomeContext = ({ venueSlug, expiresAt, source } = {}) => {
    if (!venueSlug || typeof venueSlug !== 'string') return
    const expiryMs = typeof expiresAt === 'number' ? expiresAt : Date.parse(expiresAt)
    if (!Number.isFinite(expiryMs)) return

    localStorage.setItem(
      VENUE_HOME_CONTEXT_KEY,
      JSON.stringify({
        venueSlug,
        expiresAt: expiryMs,
        source: source || null,
      })
    )
  }

  getVenueHomeContext = () => {
    const raw = localStorage.getItem(VENUE_HOME_CONTEXT_KEY)
    if (!raw) return null

    try {
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return null
      if (!parsed.venueSlug || typeof parsed.venueSlug !== 'string') return null
      if (!Number.isFinite(parsed.expiresAt)) return null
      return parsed
    } catch (error) {
      return null
    }
  }

  clearVenueHomeContext = () => {
    localStorage.removeItem(VENUE_HOME_CONTEXT_KEY)
  }

  setOauthReturnTarget = (target) => {
    if (!target || typeof target !== 'string') return
    localStorage.setItem(OAUTH_RETURN_TARGET_KEY, target)
  }

  getOauthReturnTarget = () => {
    const target = localStorage.getItem(OAUTH_RETURN_TARGET_KEY)
    return target || null
  }

  clearOauthReturnTarget = () => {
    localStorage.removeItem(OAUTH_RETURN_TARGET_KEY)
  }

  hasActiveVenueHomeContext = (now = Date.now()) => {
    const context = this.getVenueHomeContext()
    if (!context) return false
    if (context.expiresAt <= now) {
      this.clearVenueHomeContext()
      return false
    }
    return true
  }

  setGuestIdentitySnapshot = (snapshot = {}) => {
    if (!snapshot || typeof snapshot !== 'object') return
    const nextSnapshot = {
      id: snapshot.id ? String(snapshot.id) : null,
      name: snapshot.name ? String(snapshot.name) : null,
      avatarUrl: snapshot.avatarUrl ? String(snapshot.avatarUrl) : null,
      createdAt: snapshot.createdAt ? String(snapshot.createdAt) : null,
      claimedAt: snapshot.claimedAt ? String(snapshot.claimedAt) : null,
      capturedAt: snapshot.capturedAt || new Date().toISOString(),
    }
    localStorage.setItem(GUEST_IDENTITY_SNAPSHOT_KEY, JSON.stringify(nextSnapshot))
  }

  getGuestIdentitySnapshot = () => {
    const raw = localStorage.getItem(GUEST_IDENTITY_SNAPSHOT_KEY)
    if (!raw) return null

    try {
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return null
      return {
        id: parsed.id ? String(parsed.id) : null,
        name: parsed.name ? String(parsed.name) : null,
        avatarUrl: parsed.avatarUrl ? String(parsed.avatarUrl) : null,
        createdAt: parsed.createdAt ? String(parsed.createdAt) : null,
        claimedAt: parsed.claimedAt ? String(parsed.claimedAt) : null,
        capturedAt: parsed.capturedAt ? String(parsed.capturedAt) : null,
      }
    } catch (error) {
      return null
    }
  }

  clearGuestIdentitySnapshot = () => {
    localStorage.removeItem(GUEST_IDENTITY_SNAPSHOT_KEY)
  }

  clearLegacyGuestHasSelfieByUserId = (userId) => {
    if (!userId) return
    localStorage.removeItem(`${LEGACY_GUEST_SELFIE_PREFIX}${userId}`)
  }

  clearAllLegacyGuestHasSelfie = () => {
    const keysToDelete = []
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      if (key && key.startsWith(LEGACY_GUEST_SELFIE_PREFIX)) {
        keysToDelete.push(key)
      }
    }
    keysToDelete.forEach((key) => localStorage.removeItem(key))
  }

  hasAuthMeta = () => {
    const meta = this.getAuthMeta()
    return Boolean(meta?.['auth-token'])
  }

}
